import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import { client, scheduleInKinnser } from '../../services/api';
import type {
  ScheduleInKinnserResponse,
  TaskCreateTabName,
} from '../../services/api';

export type KinnserSchedulingResult =
  | ScheduleInKinnserResponse
  | { success: false; message: string };

interface UseKinnserSchedulingOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  taskName: string;
  tabName?: TaskCreateTabName | null;
  taskDate?: string | null;
}

interface UseKinnserSchedulingReturn {
  schedulingInKinnser: boolean;
  schedulingResult: KinnserSchedulingResult | null;
  handleScheduleInKinnser: () => Promise<void>;
}

/**
 * Hook for scheduling a visit (ROC or SOC) on an existing Kinnser episode.
 *
 * The backend encapsulates the multi-step flow (create task → upload → update details)
 * and records push_failed / Slack notification for 5xx/unexpected failures.
 */
export function useKinnserScheduling({
  intakeId,
  intakeStatus,
  taskName,
  tabName = 'nursing',
  taskDate,
}: UseKinnserSchedulingOptions): UseKinnserSchedulingReturn {
  const [schedulingInKinnser, setSchedulingInKinnser] = useState(false);
  const [schedulingResult, setSchedulingResult] =
    useState<KinnserSchedulingResult | null>(null);

  const clearSchedulingResult = useCallback(() => {
    setSchedulingResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearSchedulingResult
  );

  const handleScheduleInKinnser = useCallback(async () => {
    setSchedulingInKinnser(true);
    setSchedulingResult(null);

    try {
      if (!intakeId?.trim()) {
        setSchedulingResult({ success: false, message: 'No intake selected' });
        return;
      }

      const response = await scheduleInKinnser({
        client,
        path: { intake_id: intakeId },
        body: {
          task_name: taskName,
          tab_name: tabName ?? undefined,
          task_date: taskDate ?? undefined,
        },
      });
      setSchedulingResult(
        response.data ?? {
          success: false,
          message: 'Scheduling failed. Please try again.',
        }
      );
    } catch (error) {
      console.error('Failed to schedule visit in Kinnser:', error);
      setSchedulingResult({
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setSchedulingInKinnser(false);
    }
  }, [intakeId, taskName, tabName, taskDate]);

  return {
    schedulingInKinnser,
    schedulingResult,
    handleScheduleInKinnser,
  };
}
